import { toCents, toEuros } from '../money';
import type { Plan, PlannedDay } from '../plan';
import type { Recipe } from '../recipe';
import type { ShoppingResult } from '../shopping';
import { dinnerAlternatives, portionCents } from './alternatives';
import { periodBudget } from './budget';
import { replaceDinner } from './edit';
import { recipeOf, shopForPlan, type PlanRequest } from './request';

function cheapestAlternative(
  plan: Plan,
  day: PlannedDay,
  request: PlanRequest,
): Recipe | undefined {
  const current = portionCents(recipeOf(request, day.dinner.recipeId), day.date, request);
  const [cheapest] = dinnerAlternatives(plan, day.date, request);
  return cheapest && portionCents(cheapest, day.date, request) < current ? cheapest : undefined;
}

function replaceMostExpensive(plan: Plan, request: PlanRequest): Plan | undefined {
  const byCost = plan.days
    .filter((day) => !day.dinner.locked)
    .map((day) => ({
      day,
      cost: portionCents(recipeOf(request, day.dinner.recipeId), day.date, request),
    }))
    .sort((a, b) => b.cost - a.cost);
  for (const { day } of byCost) {
    const alternative = cheapestAlternative(plan, day, request);
    if (alternative) return replaceDinner(plan, day, alternative, request);
  }
  return undefined;
}

/**
 * DE: Liegt der Plan über dem Budget, die teuersten Abendessen durch günstigere gültige ersetzen.
 * Bleibt eine Lücke, kommt eine Warnung mit dem fehlenden Betrag dazu.
 * EN: When the plan is over budget, replace the most expensive dinners with cheaper valid ones.
 * If a gap remains, a warning with the missing amount is added.
 */
export function repairBudget(
  draft: { plan: Plan; shopping: ShoppingResult },
  request: PlanRequest,
): { plan: Plan; shopping: ShoppingResult } {
  const budgetCents = toCents(periodBudget(request.household, request.period));
  let { plan, shopping } = draft;
  for (
    let round = 0;
    round < request.period.days * 2 && toCents(shopping.total) > budgetCents;
    round++
  ) {
    const next = replaceMostExpensive(plan, request);
    if (!next) break;
    plan = next;
    shopping = shopForPlan(plan, request);
  }
  const gapCents = toCents(shopping.total) - budgetCents;
  if (gapCents <= 0) return { plan, shopping };
  const warning = { code: 'over-budget' as const, amount: toEuros(gapCents) };
  return { plan: { ...plan, warnings: [...plan.warnings, warning] }, shopping };
}
